import { Stack } from '@mui/material';
import { ROUTES } from 'config';
import {
  AutocompleteInput,
  Button,
  DateTimeInput,
  Modal,
  TextField,
} from 'melp-design/components';
import moment from 'moment';
import { FC } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { useMe } from 'state/Administrators';
import { useCompanies } from 'store/companies';
import { useCreateEmployee } from 'store/employees';
import { ModalProps } from 'store/modal';
import { getTimezoneFromDate } from 'utils/Common';
import { stripPhone } from 'utils/general';
import { routerHistory } from 'utils/router-history';
import { usePredefinedToasts } from 'utils/Toast';

interface Values {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  personalCode: string;
  companyId: string;
  startDate: string;
  externalId: string;
}

interface Props extends ModalProps {}

export const CreateEmployeeModal: FC<Props> = ({ closeModal }) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const { me, canEditCompanies } = useMe();

  const { handleSubmit, control, watch } = useForm<Values>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      personalCode: '',
      companyId: '',
      externalId: '',
      startDate: moment().format(),
    },
  });

  const { data: companies, isLoading: isCompaniesLoading } = useCompanies({
    status: ['active'],
    pageSize: 99999,
  });

  const { mutate: createEmployee, isLoading: isCreating } = useCreateEmployee();

  const onSubmit: SubmitHandler<Values> = (values) => {
    createEmployee(
      {
        ...values,
        email: values.email || null,
        phone: values.phone ? stripPhone(values.phone) : null,
        personalCode: values.personalCode || null,
        startDateTZ: getTimezoneFromDate(values.startDate),
        externalId: me?.parentCompany?.settings.useExternalId
          ? values.externalId
          : undefined,
      },
      {
        onSuccess: ({ data }) => {
          predefinedToasts.success.added();
          closeModal();
          routerHistory.push(ROUTES.employees.details.replace(':id', data.id));
        },
      },
    );
  };

  const [email, phone] = watch(['email', 'phone']);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('employees.add_employee')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.create'),
            type: 'submit',
            disabled: isCreating,
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="firstName"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('form.first_name')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="lastName"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('form.last_name')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('form.work_email')}
                name={name}
                value={value}
                onChange={onChange}
                required={!phone}
                ref={ref}
              />
            )}
          />

          <Controller
            name="phone"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('form.mobile_number')}
                name={name}
                value={value}
                onChange={onChange}
                required={!email}
                ref={ref}
              />
            )}
          />

          <Controller
            name="personalCode"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('form.personal_code')}
                name={name}
                value={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          {me?.parentCompany?.settings.useExternalId ? (
            <Controller
              name="externalId"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <TextField
                  label={t('employees.externalId.label')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  ref={ref}
                />
              )}
            />
          ) : null}

          <Stack flexDirection="row" gap={2}>
            <Controller
              name="companyId"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <AutocompleteInput
                  label={t('employees.company')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  options={
                    companies?.items.map((company) => ({
                      label: company.name,
                      value: company.id,
                    })) ?? []
                  }
                  required
                  disabled={isCompaniesLoading}
                  ref={ref}
                />
              )}
            />

            {canEditCompanies ? (
              <Button
                label={t('form.manage')}
                variant="tertiary"
                onClick={() => {
                  closeModal();
                  routerHistory.push(
                    '/more/company-settings?tab=companySettingsCompanies',
                  );
                }}
              />
            ) : null}
          </Stack>

          <Controller
            name="startDate"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <DateTimeInput
                label={t('employees.activate_on')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
